import api from '../../utils/api';
import { MedicalStoreProfile } from '../../models/MasterAdmin/medicalStore';
import { Order, OrderStats } from '../../models/MasterAdmin/order';
import type { User } from '../../models/MasterAdmin/user';

function mapOrderStatusFromDb(status: unknown): string {
  const s = String(status ?? '').toLowerCase();
  if (['delivered', 'refund_completed'].includes(s)) return 'Delivered';
  if (['cancelled', 'cancelled_before_pickup'].includes(s)) return 'Cancelled';
  return 'Pending';
}

function mapPaymentStatusFromDb(paymentStatus: unknown): string {
  const s = String(paymentStatus ?? '').toLowerCase();
  if (s === 'paid') return 'Paid';
  if (s === 'failed') return 'Failed';
  if (['refunded', 'partially_refunded', 'processing_refund'].includes(s)) return 'Refunded';
  return 'Unpaid';
}

/** Ensures `User` is never missing on the object (avoids `User` vs `user` and undefined `.name` crashes). */
function normalizeVendorStatsOrder(raw: Record<string, unknown>): Order {
  const u = (raw.User ?? raw.user) as User | null | undefined;
  const userId = typeof raw.userId === 'string' ? raw.userId : '';
  const patientName =
    typeof raw.patientName === 'string' ? raw.patientName : null;

  let User: User | null = u ?? null;
  if (!User && userId) {
    User = {
      userId,
      name: patientName || '—',
      emailId: '',
      status: true,
      phone_number: undefined,
    };
  }

  const orderStatus =
    typeof raw.orderStatus === 'string' && raw.orderStatus.length > 0
      ? raw.orderStatus
      : mapOrderStatusFromDb(raw.status);
  const paymentStatus = mapPaymentStatusFromDb(raw.paymentStatus);

  return {
    ...(raw as unknown as Order),
    User,
    orderStatus: orderStatus as Order['orderStatus'],
    paymentStatus: paymentStatus as Order['paymentStatus'],
  };
}

export const fetchMedicalStoreProfile = async (vendorId: string): Promise<MedicalStoreProfile> => {
  try {
    const response = await api.get(`/master-admin/medical-store/${vendorId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching medical store profile:', error);
    throw error;
  }
};

export const fetchMedicalStoreOrders = async (vendorId: string): Promise<OrderStats> => {
  try {
    const response = await api.get(`/master-admin/orders/vendor/${vendorId}/stats`);
    const data = response.data as OrderStats & { orders?: unknown[] };
    const rawList: unknown[] = Array.isArray(data.orders) ? data.orders : [];
    const orders = rawList
      .filter((o): o is Record<string, unknown> => o != null && typeof o === 'object' && !Array.isArray(o))
      .map((o) => normalizeVendorStatsOrder(o));

    return {
      ...data,
      orders,
    };
  } catch (error) {
    console.error('Error fetching medical store orders:', error);
    throw error;
  }
};

/** Full medicine order (line items, address, prescription) for admin detail view */
export const fetchMedicineOrderDetail = async (vendorId: string, orderId: string) => {
  try {
    const response = await api.get(
      `/master-admin/orders/vendor/${encodeURIComponent(vendorId)}/medicine-order/${encodeURIComponent(orderId)}`
    );
    return response.data as Record<string, unknown>;
  } catch (error) {
    console.error('Error fetching medicine order detail:', error);
    throw error;
  }
};

export const fetchVendorMedicineInvoiceBlob = async (orderId: string): Promise<Blob> => {
  const encodedOrderId = encodeURIComponent(orderId);
  const response = await api.get(`/medicine-delivery/invoice/${encodedOrderId}`, {
    params: { userType: 'vendor' },
    responseType: 'blob',
  });

  return response.data as Blob;
};
